import { useEffect, useMemo, useRef, useState, type ComponentType } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { agentQueryOptions } from '@/entities/agent';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { getModelDisplayLabel } from '@/features/chats/lib/getModelDisplayLabel';
import { ChatStoreContext, createChatStore } from '@/features/chats/stores';
import { Registry } from '../ChatOptions/ChatOptions.registry';
import type { ChatOptionsProps } from '../ChatOptions/ChatOptions.types';
import type { ImageModel } from '../ChatOptions/ImageGeneration/ImageGenerationOptions.types';
import { getOptionsForModel as imageOptionsFor } from '../ChatOptions/ImageGeneration/ImageOptionsHelpers';
import { ModelsDropdown } from '../ChatOptions/ModelsDropdown';
import type { VideoModel } from '../ChatOptions/VideoGeneration/VideoGenerationOptions.types';
import { getOptionsForModel as videoOptionsFor } from '../ChatOptions/VideoGeneration/videoOptionsHelpers';

type Settings = Record<string, unknown>;

/**
 * The agents whose settings can be fixed outside a chat, each with what picking a model resets
 * the rest to. Both keep their model inside the settings they send, which is what lets a flow node
 * hold all of it.
 */
const SETTINGS_FOR_MODEL: Partial<Record<string, (model: string) => Settings>> = {
  'image-generation': (model) =>
    imageOptionsFor(model as ImageModel | 'custom-workflow') as unknown as Settings,
  'video-generation': (model) => videoOptionsFor(model as VideoModel) as unknown as Settings,
};

/** Whether the composer has settings of this agent's to fix. */
export function hasComposerSettings(agentId: string): boolean {
  return agentId in SETTINGS_FOR_MODEL && agentId in Registry;
}

/** What the composer wrote, less the fields it leaves unset, which a stored copy should not keep. */
function stored(options: unknown): Settings {
  return JSON.parse(JSON.stringify(options ?? {}, (_key, value: unknown) => value ?? undefined));
}

const same = (a: unknown, b: unknown) => JSON.stringify(stored(a)) === JSON.stringify(stored(b));

export interface AgentComposerSettingsProps {
  agentId: string;
  /** The settings to show, as stored. None shows what a new chat with the agent starts on. */
  value: Settings | undefined;
  /** Called with every change the person makes, never for what is merely shown. */
  onChange: (value: Settings) => void;
  /**
   * Offer only the agent's own models, leaving out what the composer adds beside them (an image
   * custom workflow), which a Generate image node cannot hold.
   */
  modelsOnly?: boolean;
}

/**
 * The chat composer's own settings for an agent, its model included, outside a chat: drawn over a
 * chat store of their own, seeded from `value`, so they behave exactly as they do in the composer.
 * Nothing for an agent whose settings the composer cannot fix this way.
 */
export function AgentComposerSettings({
  agentId,
  value,
  onChange,
  modelsOnly,
}: AgentComposerSettingsProps) {
  const { t } = useTranslation('chats');
  const workspaceId = useCurrentWorkspaceId();
  const { data: agent } = useQuery({
    ...agentQueryOptions(agentId, workspaceId),
    enabled: hasComposerSettings(agentId),
  });
  const [store] = useState(createChatStore);
  const options = store((state) => state.chatInput.options);
  const seededFor = store((state) => state.agent);
  const forModel = SETTINGS_FOR_MODEL[agentId];
  const Options = Registry[agentId] as ComponentType<ChatOptionsProps> | undefined;
  const enabledModels = agent?.enabledModels;

  const shown = useMemo(
    () => value ?? (enabledModels?.[0] && forModel ? forModel(enabledModels[0]) : undefined),
    [value, enabledModels, forModel],
  );
  // What `onChange` last reported or was handed, so the store echoing it back is not a change.
  const known = useRef<unknown>(shown);

  useEffect(() => {
    if (!agent) return;
    known.current = shown;
    store.setState((state) => ({
      agent,
      currentAgentId: agent.id,
      chatInput: { ...state.chatInput, options: shown },
    }));
  }, [agent, shown, store]);

  useEffect(
    () =>
      store.subscribe((state, previous) => {
        const next = state.chatInput.options;
        if (next === previous.chatInput.options || same(next, known.current)) return;
        known.current = next;
        onChange(stored(next));
      }),
    [store, onChange],
  );

  // Not before the store holds the seeded settings: the composer writes its own defaults into an
  // empty store as it mounts, which would read as a change the person made.
  if (!agent || !Options || !forModel || seededFor !== agent) return null;
  // Nothing to pick from, and the form would write its own default model in as it mounts.
  if (modelsOnly && !enabledModels?.length) {
    return <p className="text-sm text-foreground-subtle">{t('chats.chatInputV2.noImageModels')}</p>;
  }

  const model = (options?.settings as { model?: string } | undefined)?.model;
  const models = (enabledModels ?? []).map((id) => ({
    value: id,
    label: getModelDisplayLabel(id),
  }));

  return (
    <ChatStoreContext.Provider value={store}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <span className="text-xs font-medium text-foreground-subtle">
            {t('chats.chatInputV2.aiModel')}
          </span>
          <ModelsDropdown
            label={t('chats.chatInputV2.selectModel')}
            description=""
            options={models}
            initialValue={model ?? ''}
            value={model ?? ''}
            menuPosition="below"
            onChange={(next) => store.getState().setInputOptions(forModel(next))}
          />
        </div>
        <Options
          isLoading={false}
          setIsLoading={() => undefined}
          onOptionUpdated={store.getState().setInputOptions}
          options={options}
          attachments={[]}
          attachmentsCount={0}
          {...(modelsOnly ? { models: enabledModels ?? [] } : {})}
        />
      </div>
    </ChatStoreContext.Provider>
  );
}
